// Service to load and parse HKDSE question files
class HKDSEQuestionLoader {
  constructor() {
    this.questionData = {};
    this.questionList = [];
  }

  // Load all HKDSE question files
  async loadAllQuestions() {
    const years = [2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025];
    
    for (const year of years) {
      try {
        const questions = await this.loadYearQuestions(year);
        this.questionData[year] = questions;
        this.questionList.push(...questions.map(q => ({
          ...q,
          year,
          displayText: `${year} Q.${q.number}`
        })));
      } catch (error) {
        console.warn(`Failed to load ${year} questions:`, error);
      }
    }

    return this.questionList;
  }

  // Load questions for a specific year
  async loadYearQuestions(year) {
    try {
      // For now, return sample questions for each year
      // In a production environment, you would load the actual files
      const sampleQuestions = this.getSampleQuestions(year);
      return sampleQuestions;
    } catch (error) {
      console.error(`Error loading ${year} questions:`, error);
      return [];
    }
  }

  // Get sample questions for each year
  getSampleQuestions(year) {
    const baseQuestions = [
      {
        number: 1,
        marks: "3 marks",
        content: `Question 1 (3 marks) - Simplify the expression`,
        parts: [],
        figureDescription: '',
        problem: 'Simplify the given mathematical expression.'
      },
      {
        number: 2,
        marks: "4 marks",
        content: `Question 2 (4 marks) - Factorize the expression`,
        parts: [],
        figureDescription: '',
        problem: 'Factorize the given polynomial expression.'
      },
      {
        number: 3,
        marks: "5 marks",
        content: `Question 3 (5 marks) - Solve the equation`,
        parts: [],
        figureDescription: '',
        problem: 'Solve the given equation for the unknown variable.'
      },
      {
        number: 4,
        marks: "6 marks",
        content: `Question 4 (6 marks) - Triangle geometry`,
        parts: [],
        figureDescription: 'Triangle ABC with points D, E, M on side BC. Lines AD, AE, and AM are drawn from vertex A to these points on the base.',
        problem: 'In Figure 1, ABC is a triangle. D, E and M are all points on BC such that BD = CE, ∠ADC = ∠AEB and DM = EM.'
      },
      {
        number: 5,
        marks: "7 marks",
        content: `Question 5 (7 marks) - Circle geometry`,
        parts: [],
        figureDescription: 'Circle with points A, B, C, D on the circumference. Point E is located on chord AC.',
        problem: 'In Figure 2, ABCD is a circle. E is a point on AC such that BC = CE.'
      }
    ];

    return baseQuestions.map(q => ({
      ...q,
      year,
      content: `${q.content} (${year})`,
      problem: `${q.problem} (${year} version)`
    }));
  }

  // Parse questions from file content
  parseQuestions(content, year) {
    const questions = [];
    const lines = content.split('\n');
    
    let currentQuestion = null;
    let currentPart = null;
    let inQuestion = false;
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip empty lines and headers
      if (!line || line.startsWith('HKDSE') || line.includes('Paper 1 LaTeX')) {
        continue;
      }
      
      // Check for question start
      const questionMatch = line.match(/Question\s+(\d+)\s*\(([^)]+)\)/i);
      if (questionMatch) {
        // Save previous question if exists
        if (currentQuestion) {
          questions.push(currentQuestion);
        }
        
        // Start new question
        currentQuestion = {
          number: parseInt(questionMatch[1]),
          marks: questionMatch[2],
          year: year,
          content: line,
          parts: [],
          figureDescription: '',
          problem: ''
        };
        inQuestion = true;
        currentPart = null;
        continue;
      }
      
      // If we're in a question, collect content
      if (inQuestion && currentQuestion) {
        // Check for figure description
        if (line.toLowerCase().includes('figure') && line.toLowerCase().includes('description')) {
          currentQuestion.figureDescription = line;
          continue;
        }
        
        // Check for problem statement (usually contains geometric terms)
        if (line.includes('Figure') || 
            line.includes('triangle') || 
            line.includes('circle') || 
            line.includes('square') ||
            line.includes('ABCD') ||
            line.includes('ABC')) {
          currentQuestion.problem = line;
          continue;
        }
        
        // Check for part headers
        const partMatch = line.match(/^\(([a-z])\)/i);
        if (partMatch) {
          currentPart = {
            id: partMatch[1].toLowerCase(),
            question: line.replace(/^\([a-z]\)\s*/i, ''),
            marks: 2
          };
          currentQuestion.parts.push(currentPart);
          continue;
        }
        
        // Check for sub-parts
        const subPartMatch = line.match(/^\(([ivx]+)\)/i);
        if (subPartMatch && currentPart) {
          const subPart = {
            id: subPartMatch[1].toLowerCase(),
            question: line.replace(/^\([ivx]+\)\s*/i, ''),
            marks: 2
          };
          currentPart.subParts = currentPart.subParts || [];
          currentPart.subParts.push(subPart);
          continue;
        }
        
        // Add line to current part or question
        if (currentPart) {
          if (!currentPart.question.includes(line)) {
            currentPart.question += ' ' + line;
          }
        } else if (currentQuestion) {
          currentQuestion.content += '\n' + line;
        }
      }
    }
    
    // Add the last question
    if (currentQuestion) {
      questions.push(currentQuestion);
    }
    
    return questions;
  }

  // Get question by year and number
  getQuestion(year, number) {
    const yearQuestions = this.questionData[year];
    if (!yearQuestions) return null;
    
    return yearQuestions.find(q => q.number === number);
  }

  // Get all questions as a flat list for dropdown
  getAllQuestions() {
    return this.questionList;
  }

  // Get questions by year
  getQuestionsByYear(year) {
    return this.questionData[year] || [];
  }

  // Search questions by text
  searchQuestions(searchText) {
    const searchLower = searchText.toLowerCase();
    return this.questionList.filter(q => 
      q.content.toLowerCase().includes(searchLower) ||
      q.problem.toLowerCase().includes(searchLower) ||
      q.figureDescription.toLowerCase().includes(searchLower)
    );
  }

  // Get question content for input
  getQuestionContent(question) {
    let content = `Question ${question.number} (${question.marks})\n`;
    content += question.problem + '\n\n';
    
    if (question.figureDescription && question.figureDescription.trim()) {
      content += `Figure description: ${question.figureDescription}\n\n`;
    }
    
    question.parts.forEach(part => {
      content += `(${part.id}) ${part.question}\n`;
      
      if (part.subParts) {
        part.subParts.forEach(subPart => {
          content += `(${subPart.id}) ${subPart.question}\n`;
        });
      }
    });
    
    return content;
  }
}

export default new HKDSEQuestionLoader(); 